/**
 * Just-posted splice (spec 1.3): a pure, client-side guarantee that the questions a
 * user just posted stay at the TOP of the meetverse feed for JUST_POSTED_SPLICE_MS
 * on the posting device.
 *
 * The TECH-305 optimistic prepend (useCreateQuestionCache) already covers the
 * create-path: it writes the post's id into the cached first page and never
 * invalidates. The hole it cannot cover is a refetch inside the fan-out window
 * (manual pull-to-refresh, SSE invalidation): the server (Redis feed) may return a
 * page 1 that does not contain the post yet, or contains it below newer posts,
 * replacing the cached ids wholesale. Registering the post here and splicing at
 * select-time closes that hole without touching the network or the cached server data.
 *
 * NORMALIZED CACHE: feed pages store `{ ids: string[] }`, not full entities — the
 * question bodies live in the entity store (questionEntityKey), seeded by the create
 * path (upsertQuestionEntities) and refreshed by every server fetch. So this module
 * only tracks WHICH ids stay pinned and until when; it never carries question content.
 * The spliced id resolves to its entity via useQuestionEntities on the read side.
 *
 * Lifecycle: registered on create (useCreateQuestionCache), pin dropped when an edit
 * moves the post out of meetverse visibility (updateJustPosted), removed on delete
 * (clearJustPosted(id)), wiped on sign-out (clearAuthState) - the registry is module
 * state BY DESIGN (the guarantee is posting-device-only), so `queryClient.clear()`
 * alone cannot reach it.
 *
 * Purity contract (load-bearing for React Query select memoization): when there are
 * no live registrations, or the feed already has every registered id in place with no
 * duplicates, the SAME object reference passed in is returned - no clone. Cache data
 * is never mutated.
 */
import type { InfiniteData } from '@tanstack/react-query';
import { QuestionVisibility } from '@models/api/question';
import type { QuestionRead } from '@models/api';
import type { NormalizedPage } from '@lib/cache/questions';
import { MEETVERSE_FEED_STALE_TIME_MS } from './constants';

/**
 * Splice window, derived from the meetverse staleTime so the two cannot drift: the
 * pin must survive at least as long as the feed can refetch inside the fan-out-lag
 * period. (They measure from different clocks - post creation vs last fetch - so
 * this is a coupled floor, not an exact equivalence.)
 */
export const JUST_POSTED_SPLICE_MS = MEETVERSE_FEED_STALE_TIME_MS;

/**
 * Concrete meetverse page/envelope (what useInfiniteMeetverseQuestions' queryFn
 * returns after normalization). Deliberately NOT generic: the select callsite knows
 * this shape statically, and typing it here turns page-shape drift into a compile
 * error instead of a silent no-op splice.
 */
export type MeetverseFeedPage = NormalizedPage;
export type MeetverseFeedData = InfiniteData<MeetverseFeedPage>;

// Insertion-ordered registry: id -> registration timestamp, latest registration last.
// Every live registration is guaranteed a top slot of the feed, newest first - posting
// twice inside the window must not drop the first post's guarantee. Content is NOT held
// here (it lives in the entity store); only id + timestamp.
const registrations = new Map<string, number>();

/**
 * Remember a post this device just created. Called from the create mutation's
 * meetverse prepend path. All live registrations are pinned, newest first.
 */
export const registerJustPosted = (question: QuestionRead, now: number = Date.now()): void => {
  // Delete-then-set so re-registering an id refreshes its recency slot.
  registrations.delete(question.id);
  registrations.set(question.id, now);
};

/**
 * Called after a successful owner mutation (edit, mark as answered). The edited
 * content renders from the entity store (updateQuestionEntity), so this only adjusts
 * the PIN: editing away from meetverse visibility drops the registration - the post no
 * longer belongs in this feed. Same-visibility edits keep the original pin window;
 * editing does not extend it.
 */
export const updateJustPosted = (question: Pick<QuestionRead, 'id' | 'visibility'>): void => {
  if (!registrations.has(question.id)) return;
  if (question.visibility !== QuestionVisibility.anyone) {
    registrations.delete(question.id);
  }
};

/**
 * Drop one registration (post deleted) or all of them (sign-out, test cleanup).
 */
export const clearJustPosted = (questionId?: string): void => {
  if (questionId === undefined) {
    registrations.clear();
    return;
  }
  registrations.delete(questionId);
};

/**
 * Pure select-level splice for the normalized meetverse InfiniteData.
 *
 * Within the window, ensures the registered ids are the FIRST entries of
 * pages[0].ids, newest registration first: moves each there if present anywhere in the
 * feed (deduped across ALL pages), or prepends it if absent. With no live
 * registrations, or when the feed is already in exactly that arrangement, returns
 * `data` unchanged (same reference). Expired registrations are reaped on read.
 *
 * Only ids are reordered here - the entity content each id resolves to is owned by the
 * entity store, so there is no "prefer the fresher in-feed copy" concern: a later
 * server fetch upserts the fresh entity by id regardless of the id's page position.
 */
export const spliceJustPosted = (
  data: MeetverseFeedData,
  now: number = Date.now(),
): MeetverseFeedData => {
  if (registrations.size === 0) return data;

  for (const [id, ts] of registrations) {
    const elapsed = now - ts;
    // Negative elapsed = the wall clock stepped backwards (NTP correction, manual
    // clock change). Treat as expired: dropping the guarantee beats pinning the
    // post for the whole duration of the skew.
    if (elapsed < 0 || elapsed >= JUST_POSTED_SPLICE_MS) {
      registrations.delete(id);
    }
  }
  if (registrations.size === 0) return data;

  const pages = data.pages;
  if (pages.length === 0) return data;

  // Strip every registered id from every page (the id is re-inserted at the head of
  // page 0 below, so any later copy must go or the same id renders as two cards -
  // duplicate React keys in the flattened list). Untouched pages keep their reference.
  const registeredIds = new Set(registrations.keys());
  const strippedPages = pages.map(page => {
    const ids = page.ids;
    let filtered: string[] | null = null;
    for (let index = 0; index < ids.length; index += 1) {
      const id = ids[index];
      if (registeredIds.has(id)) {
        filtered ??= ids.slice(0, index);
      } else {
        filtered?.push(id);
      }
    }
    return filtered === null ? page : { ...page, ids: filtered };
  });

  // Desired head: newest registration first.
  const head = [...registrations.keys()].reverse();
  const nextFirstIds = [...head, ...strippedPages[0].ids];

  // Steady state after the optimistic prepend: everything already in place and no
  // stray copies elsewhere - keep the reference so select memoization stays intact.
  const originalFirstIds = pages[0].ids;
  const firstPageUnchanged =
    nextFirstIds.length === originalFirstIds.length &&
    nextFirstIds.every((id, index) => id === originalFirstIds[index]);
  const laterPagesUnchanged = strippedPages.every(
    (page, index) => index === 0 || page === pages[index],
  );
  if (firstPageUnchanged && laterPagesUnchanged) return data;

  const firstPage = firstPageUnchanged ? pages[0] : { ...strippedPages[0], ids: nextFirstIds };

  return { ...data, pages: [firstPage, ...strippedPages.slice(1)] };
};
